import { useSnackbars } from '../../app/error-handling/SnackBars';
import { useCreateNewLearningOpportunityMutation, useUpdateLearningOpportunityMutation } from '../api';
import type { LearningOpportunity, LearningOpportunityCreate } from '../data-contract';
import { buildLearningOpportunityUpdatePayload } from '../helpers';
import { useInvalidateLearningOpportunityRelatedCaches } from '../use-invalidate-related-caches';

export const useSidepanelMutations = ({
    close,
    data,
    isCreate,
    opportunityId,
    setMode,
}: {
    readonly close: () => void;
    readonly data: LearningOpportunity | undefined;
    readonly isCreate: boolean;
    readonly opportunityId: string | undefined;
    readonly setMode: (next: 'edit' | 'view') => void;
}): {
    readonly handlePublish: () => void;
    readonly handleSubmit: (payload: LearningOpportunityCreate) => void;
    readonly isPublishing: boolean;
    readonly isSubmitting: boolean;
} => {
    const createMutation = useCreateNewLearningOpportunityMutation('postJson');
    const updateMutation = useUpdateLearningOpportunityMutation('patchJson', opportunityId ?? '');
    const { showErrorSnackbar, showSuccessSnackbar } = useSnackbars();
    const invalidateRelatedCaches = useInvalidateLearningOpportunityRelatedCaches();

    const onCreateSuccess = (): void => {
        showSuccessSnackbar('success.learning-opportunities.created');
        invalidateRelatedCaches();
        close();
    };
    const onUpdateSuccess = (): void => {
        showSuccessSnackbar('success.learning-opportunities.edited');
        invalidateRelatedCaches();
        setMode('view');
    };
    const handleSubmit = (payload: LearningOpportunityCreate): void => {
        if (isCreate) {
            createMutation.mutate(payload, { onError: showErrorSnackbar, onSuccess: onCreateSuccess });
            return;
        }
        if (data === undefined) return;
        updateMutation.mutate(payload, { onError: showErrorSnackbar, onSuccess: onUpdateSuccess });
    };
    const handlePublish = (): void => {
        if (data === undefined) return;
        updateMutation.mutate(buildLearningOpportunityUpdatePayload(data, 'published'), {
            onError: showErrorSnackbar,
            onSuccess: () => {
                showSuccessSnackbar('success.learning-opportunities.edited');
                invalidateRelatedCaches();
            },
        });
    };

    return {
        handlePublish,
        handleSubmit,
        isPublishing: updateMutation.isPending,
        isSubmitting: isCreate ? createMutation.isPending : updateMutation.isPending,
    };
};
